'use client';

import React, { useMemo } from 'react';
import classNames from 'classnames';
import { Widget } from '@/modules';
import { NoDataView } from '../NoDataView';

interface CombinedWidgetProps {
    widget: Widget;
    data: any;
    renderSection: (widget: Widget, data: any) => React.ReactNode;
}

type CombinedSection = { type: 'chart' | 'table'; chartType?: string; flex?: number };

const DEFAULT_SECTIONS: CombinedSection[] = [
    { type: 'chart', chartType: 'bubble', flex: 1 },
    { type: 'table', flex: 1 },
];

const DEFAULT_LAYOUT = 'side-by-side' as const;
const DEFAULT_GAP_CLASS = 'gap-4';

/**
 * Per-widget-title overrides for widgets that fall back to DEFAULT_SECTIONS
 * (i.e. no widget.metadata.combined.sections of their own). Scoped by exact
 * widget title so a tweak for one widget never reflows any other combined
 * widget sharing the generic bubble+table default.
 */
const SECTIONS_BY_TITLE: Record<string, CombinedSection[]> = {
    'Audience Size By Channel': [
        { type: 'chart', chartType: 'bubble', flex: 0.8 },
        { type: 'table', flex: 1.2 },
    ],
};

const GAP_CLASS_BY_TITLE: Record<string, string> = {
    'Audience Size By Channel': 'gap-2',
};

export function CombinedWidget({
    widget,
    data,
    renderSection,
}: CombinedWidgetProps) {
    const config = widget.metadata?.combined;
    const layout = config?.layout ?? DEFAULT_LAYOUT;
    const sections = SECTIONS_BY_TITLE[widget.title] ?? config?.sections ?? DEFAULT_SECTIONS;
    const gapClass = GAP_CLASS_BY_TITLE[widget.title] ?? DEFAULT_GAP_CLASS;

    const extractedData = useMemo(() => {
        return data?.widget?.data || data?.data || data;
    }, [data]);

    if (!extractedData) return <NoDataView />;

    const isHorizontal = layout === 'side-by-side';

    return (
        <div
            className={classNames(
                'w-full h-full flex',
                gapClass,
                isHorizontal ? 'flex-row' : 'flex-col',
            )}
        >
            {sections.map((section, idx) => {
                const chartType = section.chartType ?? widget.chartType ?? 'bubble';

                const sectionWidget: Widget = section.type === 'chart'
                    ? { ...widget, type: 'chart', chartType, metadata: { ...widget.metadata, hideSeries: true } }
                    : { ...widget, type: 'table' };

                const sectionData = Array.isArray(extractedData) && extractedData[idx]
                    ? { data: extractedData[idx] }
                    : data;

                return (
                    <div
                        key={idx}
                        className="min-w-0"
                        style={{ flex: section.flex ?? 1 }}
                    >
                        {renderSection(sectionWidget, sectionData)}
                    </div>
                );
            })}
        </div>
    );
}
